import * as rq from '@tanstack/react-query';
import { format } from 'date-fns/fp';
import { keyBy } from 'lodash';
import { z } from 'zod';

import { type BodyOf, FeApi } from '@/api/fe-api';
import { appConfig } from '@/config';
import { useAuth } from '@/hooks/use-auth';
import { useMe } from '@/hooks/use-me';

import { getMockedAssets } from '../_mock_data/assets';
import { type Asset, AssetSchema } from '../entities/asset';
import { AssetType } from '../entities/asset-type';
import type { AssetsFilters } from '../entities/assets-filters';

import { assetFromApi } from './mappers/asset-mapper';

const dateKey = format('yyyy-MM-dd');

export namespace GetAllAssets {
  export const key = (input: Input) => [
    'siteview',
    'get-all-assets',
    input.siteId,
    dateKey(input.assetsFilters.date),
    input.assetsFilters.assetType,
  ];

  export type Input = {
    siteId: string;
    assetsFilters: AssetsFilters;
  };
  export type Output = {
    assets: Asset[];
    assetPerId: Record<string, Asset | undefined>;
    assetsFilters: AssetsFilters;
  };

  const visualizationBatteryLevelsFromAssetType: Record<
    AssetType,
    BodyOf<
      typeof FeApi.customerInfosV1SitesDynamicDataRetrieve
    >['visualizationBatteryLevels']
  > = {
    [AssetType.CONTAINER]: [AssetType.CONTAINER, AssetType.PCS],
    [AssetType.PCS]: [AssetType.PCS],
    [AssetType.RACK]: [AssetType.RACK],
    [AssetType.MODULE]: [AssetType.MODULE],
    [AssetType.STRING]: [AssetType.STRING],
  };

  function bodyFromInput(
    input: Input,
  ): BodyOf<typeof FeApi.customerInfosV1SitesDynamicDataRetrieve> {
    return {
      accureId: input.siteId,
      queryDate: dateKey(input.assetsFilters.date),
      visualizationBatteryLevels:
        visualizationBatteryLevelsFromAssetType[input.assetsFilters.assetType],
    };
  }

  export function useQuery(input: Input) {
    const auth = useAuth();
    const me = useMe();

    return rq.useQuery<Output>({
      keepPreviousData: true,
      queryKey: key(input),
      async queryFn(): Promise<Output> {
        if (me.config.id === '49') {
          const assets = getMockedAssets(
            input.siteId,
            input.assetsFilters.date,
            input.assetsFilters.assetType,
          );

          const assetPerId = keyBy(assets, 'id');

          return { assets, assetPerId, assetsFilters: input.assetsFilters };
        }

        const accessToken = await auth.getAccessToken();
        const body = bodyFromInput(input);
        const response = await FeApi.customerInfosV1SitesDynamicDataRetrieve(
          body,
          {
            baseUrl: `${appConfig.bffUrl}/plex`,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'x-user-name': me.displayName,
              'x-user-email': me.email,
            },
          },
        );

        if (response.status !== 200) {
          throw new Error('Failed to fetch static data');
        }

        const patchedData = response.data.dynamic_data.map(assetFromApi);
        const assets = await z.array(AssetSchema).parseAsync(patchedData);
        const assetPerId = keyBy(assets, 'id');

        return { assets, assetPerId, assetsFilters: input.assetsFilters };
      },
    });
  }
}
